import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Container
} from '@mui/material';
import {
  ArrowBack,
  Save,
} from '@mui/icons-material';
import { medicinesAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const EditMedicine = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [medicine, setMedicine] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchMedicine();
  }, [id]);

  const fetchMedicine = async () => {
    try {
      const response = await medicinesAPI.getById(id);
      setMedicine(response.data.data.medicine);
      setFormData(response.data.data.medicine);
    } catch (error) {
      enqueueSnackbar('Error fetching medicine', { variant: 'error' });
      navigate('/hospital/pharmacy/medicines');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = { ...formData };
      delete updateData.createdBy;
      
      await medicinesAPI.update(id, updateData);
      enqueueSnackbar('Medicine updated successfully!', { variant: 'success' });
      navigate('/hospital/pharmacy/medicines');
    } catch (error) {
      enqueueSnackbar('Error updating medicine', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!medicine) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)",
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/hospital/pharmacy/medicines')}
            variant="outlined"
          >
            Back to Medicines
          </Button>
          <Typography variant="h3" fontWeight="bold" sx={{ flexGrow: 1 }}>
            Edit Medicine
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
              Editing: {medicine.name}
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom>
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Medicine Name"
                    value={formData.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Generic Name"
                    value={formData.genericName || ''}
                    onChange={(e) => handleInputChange('genericName', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Brand"
                    value={formData.brand || ''}
                    onChange={(e) => handleInputChange('brand', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Manufacturer"
                    value={formData.manufacturer || ''}
                    onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Strength"
                    value={formData.strength || ''}
                    onChange={(e) => handleInputChange('strength', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Form"
                    value={formData.form || ''}
                    onChange={(e) => handleInputChange('form', e.target.value)}
                    required
                  >
                    <MenuItem value="tablet">Tablet</MenuItem>
                    <MenuItem value="capsule">Capsule</MenuItem>
                    <MenuItem value="syrup">Syrup</MenuItem>
                    <MenuItem value="injection">Injection</MenuItem>
                    <MenuItem value="ointment">Ointment</MenuItem>
                    <MenuItem value="cream">Cream</MenuItem>
                    <MenuItem value="drops">Drops</MenuItem>
                    <MenuItem value="inhaler">Inhaler</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Category"
                    value={formData.category || 'allopathy'}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    <MenuItem value="allopathy">Allopathy</MenuItem>
                    <MenuItem value="ayurvedic">Ayurvedic</MenuItem>
                    <MenuItem value="homeopathic">Homeopathic</MenuItem>
                    <MenuItem value="biochemic">Biochemic</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Schedule"
                    value={formData.schedule || 'non_scheduled'}
                    onChange={(e) => handleInputChange('schedule', e.target.value)}
                  >
                    <MenuItem value="non_scheduled">Non-Scheduled</MenuItem>
                    <MenuItem value="schedule_h">Schedule H</MenuItem>
                    <MenuItem value="schedule_h1">Schedule H1</MenuItem>
                    <MenuItem value="schedule_x">Schedule X</MenuItem>
                    <MenuItem value="narcotic">Narcotic</MenuItem>
                  </TextField>
                </Grid>

                {/* Additional Information */}
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
                    Additional Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Therapeutic Class"
                    value={formData.therapeuticClass || ''}
                    onChange={(e) => handleInputChange('therapeuticClass', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ATC Code"
                    value={formData.atcCode || ''}
                    onChange={(e) => handleInputChange('atcCode', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="HSN Code"
                    value={formData.hsnCode || ''}
                    onChange={(e) => handleInputChange('hsnCode', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description"
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={formData.isActive !== undefined ? formData.isActive : true}
                    onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  onClick={() => navigate('/hospital/pharmacy/medicines')}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={<Save />}
                >
                  {loading ? 'Updating...' : 'Update Medicine'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};